/**
 * Dynamic Enum Extractor for dev-tools
 * Extracts enum values directly from protobuf definitions instead of hardcoding
 */
// Import actual enums from protobuf definitions
import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import {
  ChargifyState,
  PaymentMethod,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  ChargifySite,
  Currency,
  CustomerType,
  DocumentSortType,
  Period,
  Product,
  RoleType,
  SubscriptionType,
  TemplateLockStatus,
  TemplatePermission,
  TemplateRecipientType,
  Tier,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { EnumValue } from "./enum-utils";
import { logger } from "./logger";

/**
 * Available protobuf enums that can be dynamically extracted
 */
const PROTOBUF_ENUMS = {
  chargifyState: ChargifyState,
  paymentMethod: PaymentMethod,
  customerType: CustomerType,
  subscriptionType: SubscriptionType,
  subscriptionSeatTypes: SubscriptionSeatTypes,
  roleType: RoleType,
  currency: Currency,
  product: Product,
  period: Period,
  chargifySite: ChargifySite,
  tier: Tier,
  documentSortType: DocumentSortType,
  templatePermission: TemplatePermission,
  templateRecipientType: TemplateRecipientType,
  templateLockStatus: TemplateLockStatus,
} as const;

/**
 * Extract enum values from a TypeScript enum object
 */
function extractEnumValues(
  enumObject: Record<string, string | number>
): EnumValue[] {
  const enumValues: EnumValue[] = [];

  // TypeScript enums have both string->number and number->string mappings
  // We only want the string->number mappings
  Object.entries(enumObject).forEach(([key, value]) => {
    if (typeof value === "number") {
      enumValues.push({
        name: key,
        value: value,
      });
    }
  });

  return enumValues;
}

/**
 * Get enum values dynamically from protobuf definitions
 */
export function getEnumValuesDynamic(fieldName: string): EnumValue[] | null {
  const lowerFieldName = fieldName.toLowerCase();

  // Map field names to enum keys
  let enumKey: keyof typeof PROTOBUF_ENUMS | null = null;

  if (lowerFieldName === "chargifystate" || lowerFieldName === "state") {
    enumKey = "chargifyState";
  } else if (
    lowerFieldName === "paymentmethod" ||
    lowerFieldName === "payment_method"
  ) {
    enumKey = "paymentMethod";
  } else if (
    lowerFieldName === "customertype" ||
    lowerFieldName === "customer_type"
  ) {
    enumKey = "customerType";
  } else if (
    lowerFieldName === "subscriptiontype" ||
    lowerFieldName === "subscription_type" ||
    lowerFieldName === "type"
  ) {
    enumKey = "subscriptionType";
  } else if (lowerFieldName === "roletype" || lowerFieldName === "role_type") {
    enumKey = "roleType";
  } else if (lowerFieldName === "currency") {
    enumKey = "currency";
  } else if (lowerFieldName === "product") {
    enumKey = "product";
  } else if (lowerFieldName === "period") {
    enumKey = "period";
  } else if (lowerFieldName === "chargifysite" || lowerFieldName === "site") {
    enumKey = "chargifySite";
  } else if (lowerFieldName === "tier") {
    enumKey = "tier";
  } else if (
    lowerFieldName === "documentsorttype" ||
    lowerFieldName === "sort_type"
  ) {
    enumKey = "documentSortType";
  } else if (
    lowerFieldName === "templatepermission" ||
    lowerFieldName === "permission"
  ) {
    enumKey = "templatePermission";
  } else if (
    lowerFieldName === "templaterecipienttype" ||
    lowerFieldName === "recipient_type"
  ) {
    enumKey = "templateRecipientType";
  } else if (
    lowerFieldName === "templatelockstatus" ||
    lowerFieldName === "lock_status"
  ) {
    enumKey = "templateLockStatus";
  } else if (
    lowerFieldName === "seattype" ||
    lowerFieldName === "seat_type" ||
    lowerFieldName === "subscriptionseattype" ||
    lowerFieldName === "subscription_seat_type"
  ) {
    enumKey = "subscriptionSeatTypes";
  }

  // Pattern-based detection for common cases
  if (!enumKey) {
    if (lowerFieldName.includes("state") || lowerFieldName.endsWith("state")) {
      enumKey = "chargifyState";
    } else if (
      lowerFieldName.includes("payment") &&
      (lowerFieldName.includes("method") || lowerFieldName.endsWith("method"))
    ) {
      enumKey = "paymentMethod";
    } else if (
      lowerFieldName.includes("type") &&
      lowerFieldName.includes("customer")
    ) {
      enumKey = "customerType";
    } else if (
      lowerFieldName.includes("type") &&
      (lowerFieldName.includes("subscription") ||
        lowerFieldName.includes("sub"))
    ) {
      enumKey = "subscriptionType";
    } else if (
      lowerFieldName.includes("type") &&
      lowerFieldName.includes("role")
    ) {
      enumKey = "roleType";
    } else if (
      lowerFieldName.includes("currency") ||
      lowerFieldName.endsWith("currency")
    ) {
      enumKey = "currency";
    } else if (
      lowerFieldName.includes("period") ||
      lowerFieldName.endsWith("period")
    ) {
      enumKey = "period";
    } else if (
      lowerFieldName.includes("tier") ||
      lowerFieldName.endsWith("tier")
    ) {
      enumKey = "tier";
    } else if (
      lowerFieldName.includes("site") &&
      (lowerFieldName.includes("chargify") ||
        lowerFieldName.includes("billing"))
    ) {
      enumKey = "chargifySite";
    } else if (
      (lowerFieldName.includes("seat") && lowerFieldName.includes("type")) ||
      lowerFieldName.endsWith("seattype")
    ) {
      enumKey = "subscriptionSeatTypes";
    }
  }

  if (!enumKey) {
    return null;
  }

  try {
    const enumObject = PROTOBUF_ENUMS[enumKey];
    if (!enumObject) {
      return null;
    }

    return extractEnumValues(enumObject as Record<string, string | number>);
  } catch (error) {
    logger.warn(`Failed to extract enum values for ${fieldName}`, { error });
    return null;
  }
}
